import * as React from 'react';
import { observer } from 'mobx-react';
import { inject } from '../utils';
import { withTheme } from 'styled-components';
import { styled } from '../utils/styled';
import { propStyles } from '../utils/propStyles';
import Translate from './Translate';
import Tooltip from './Tooltip';
import SortToggle from './SortToggle';
import { ColumnHeader } from './Grid';
import isSortableColumn from '../utils/isSortableColumn';
import getSortKeyFromColumn from '../utils/getSortKeyFromColumn';
import getColumnLabel from '../utils/getColumnLabel';
import isExistingAdvancedSort from '../utils/isExistingAdvancedSort';
import get from 'lodash/get';
import { computed } from 'mobx';
import { camelCase } from '@kurtosys/ksys-app-template/dist/utils/changeCase';
import { DataStore } from '../stores';
import { ColumnConfig } from '../models/configuration/explorer/ColumnConfig';
import { Theme } from '../models/Theme';
import Dropdown from './Dropdown';
import { Modifier, OptionsGeneric } from '@popperjs/core';

const ColumnHeaderInner = styled<any, any>(
    'div',
    'columnHeaderInner',
    'column'
)`
    ${propStyles};
`;

ColumnHeaderInner.defaultProps = {
    display: 'flex',
    alignItems: 'center',
};

const ColumnLabel = styled<any, any>('div', 'columnLabel', 'column')`
    ${propStyles};
`;

ColumnLabel.defaultProps = {
    flex: '1 1 auto',
    textAlign: 'center',
};

interface ColumnOptions {
    groupIndex: number;
    label: string;
}

interface WorkingColumnConfig extends ColumnConfig {
    options: Array<ColumnOptions>;
    columnIndex: number;
    disclaimers: Array<string>;
}

interface ColumnProps {
    theme?: Theme;
    dataStore: DataStore;
    column: WorkingColumnConfig;
    selectedGroupIndex?: number;
    rowSpan?: number;
    colSpan?: number;
}
@inject('dataStore')
@observer
class Column extends React.Component<ColumnProps> {
    constructor(props) {
        super(props);
        this.handleSortClick = this.handleSortClick.bind(this);
    }

    @computed
    get direction() {
        const { dataStore, column } = this.props;

        if (
            dataStore.advancedSort &&
            isExistingAdvancedSort(column, dataStore.advancedSort)
        ) {
            return get(dataStore, 'advancedSort.direction', '');
        } else {
            const key = getSortKeyFromColumn(column);
            return get(
                dataStore.propertySort.find(c => c.key === key),
                'direction',
                ''
            );
        }
    }

    @computed
    get dropdownTheme() {
        const { theme } = this.props;
        const result = {
            ...theme,
            // This is generated internally using the ColumnGroupLabel & ColumnDropdownGroupLabel. No theme key provided by Studio.
            ColumnDropdownButton: {
                fontFamily: 'heading',
                fontWeight: 'normal',
                margin: '0 0 0.5em 0',
                padding: '0 0 0.5em 0',
                borderWidth: '1px',
                borderStyle: 'none none solid none',
                borderColor: 'gray500',
                borderRadius: '0px',
                justifyContent: 'center',
                ...theme.ColumnGroupLabel,
                ...theme.ColumnDropdownGroupLabel,
            },
            ColumnDropdownChevron: {
                display: 'inline-block',
                verticalAlign: 'middle',
                height: '1em',
                width: '1em',
                margin: '0 0.5em',
                ...theme.ColumnDropdownChevronIcon,
            },
            ColumnDropdownWrapper: {
                flex: '1 0 auto',
                ...theme.ColumnDropdownWrapper,
            },
            ColumnDropdownList: {
                maxHeight: '300px',
                overflowX: 'hidden',
                overflowY: 'auto',
                textAlign: 'center',
                ...theme.ColumnDropdownList,
            },
            ColumnDropdownListItem: {
                fontFamily: 'body',
                fontWeight: 'normal',
                paddingY: 'sm',
                paddingX: 'md',
                borderWidth: '1px',
                borderStyle: 'none none solid none',
                borderColor: 'gray300',
                fontSize: 'sm',
                cursor: 'pointer',
                alternated: {
                    bgColor: 'gray100',
                },
                hover: {
                    bgColor: 'gray100',
                },
                selected: {
                    color: 'primary',
                    fontWeight: 'bold',
                },
                ...theme.ColumnDropdownListItem,
            },
            ColumnDropdownMenu: {
                position: 'absolute',
                top: 'calc(100% + 1px)',
                zIndex: 'dropdown',
                width: ['100%', 'auto'],
                margin: ['0', '0 10%'],
                bgColor: 'white',
                borderWidth: '1px',
                borderColor: 'gray300',
                borderStyle: 'solid',
                borderRadius: 'sm',
                boxShadow: 'sm',
                ...theme.ColumnDropdownMenu,
            },
            ColumnDropdownInnerWrapper: {
                position: 'relative',
                ...theme.ColumnDropdownInnerWrapper,
            },
            ColumnDropdownDisclaimerWrapper: {
                borderStyle: 'none none solid none',
                borderColor: '#adb5bd',
                borderRadius: '0px',
                borderWidth: '1px',
                margin: '0 0 0.5em 0',
                padding: '0 0 0.5em 0',
                ...theme.ColumnDropdownDisclaimerWrapper,
            },
        };
        return result;
    }

    @computed
    get popperOptions(): Partial<OptionsGeneric<Partial<Modifier<any, any>>>> {
        return {
            placement: 'bottom',
            modifiers: [
                {
                    name: 'offset',
                    options: {
                        offset: [0, 1],
                    },
                },
                {
                    name: 'flip',
                    enabled: false,
                },
            ],
        };
    }

    handleSortClick() {
        const { dataStore, column } = this.props;
        dataStore.toggleSort(column);
    }

    render() {
        const {
            theme,
            dataStore,
            column,
            selectedGroupIndex,
            children,
            rowSpan,
            colSpan,
            ...props
        } = this.props;

        const { columnStore } = dataStore;

        const sortable = isSortableColumn(column);
        const columnLabel = getColumnLabel(column, dataStore);

        let mappedColumnOptions;
        let selectedItem;

        if (column.type === 'dropdown') {
            mappedColumnOptions = column.options.map(option => {
                return {
                    label: option.label,
                    columnIndex: column.columnIndex,
                    id: option.groupIndex,
                };
            });
            selectedItem = {
                ...(mappedColumnOptions.find(option => {
                    return option.id === selectedGroupIndex;
                }) || {}),
                disclaimers: (column as any).mappedDisclaimers,
            };
        }

        return (
            <ColumnHeader
                {...theme.ColumnHeader}
                {...props}
                width={column.width}
                verticalAlign={column.verticalAlign}
                textAlign={column.textAlign || 'center'}
                rowSpan={rowSpan}
                colSpan={colSpan}
            >
                <ColumnHeaderInner {...theme.ColumnHeaderInner}>
                    {column.type === 'dropdown' ? (
                        <Dropdown
                            items={mappedColumnOptions}
                            selectedItem={selectedItem}
                            selectItemFn={item => {
                                columnStore.setSelectedGroup(
                                    item.columnIndex as any,
                                    item.id
                                );
                            }}
                            chevronIcon={'ColumnDropdownChevronIcon.svg'}
                            chevronIconExpanded={
                                'ColumnDropdownChevronIconExpanded.svg'
                            }
                            themeKeys={{
                                root: 'ColumnDropdownWrapper',
                                button: 'ColumnDropdownButton',
                                buttonIcon: 'ColumnDropdownChevron',
                                listWrapper: 'ColumnDropdownMenu',
                                list: 'ColumnDropdownList',
                                listItem: 'ColumnDropdownListItem',
                                wrapper: 'ColumnDropdownInnerWrapper',
                                disclaimerWrapper:
                                    'ColumnDropdownDisclaimerWrapper',
                            }}
                            theme={this.dropdownTheme}
                            popperOptions={this.popperOptions}
                            buttonAccessibilityKey={'ColumnDropdownButton'}
                        />
                    ) : (
                        column.showLabel !== false && (
                            <ColumnLabel
                                {...theme.ColumnLabel}
                                textAlign={column.textAlign || 'center'}
                            >
                                <Translate>{columnLabel}</Translate>
                                <Tooltip
                                    content={column.disclaimers}
                                    accessibilityContext={`for disclaimers of the ${columnLabel} column`}
                                    analyticsContext={{
                                        metricContext: `columnLabel-${camelCase(
                                            column.label
                                        )}`,
                                        source: 'ColumnLabel',
                                        tooltipCode: column.disclaimerTypes,
                                    }}
                                />
                            </ColumnLabel>
                        )
                    )}
                    {sortable && (
                        <SortToggle
                            direction={this.direction}
                            onClick={this.handleSortClick}
                            metricContext={'column'}
                        />
                    )}
                </ColumnHeaderInner>
                {children}
            </ColumnHeader>
        );
    }
}

export default withTheme(Column) as any;



// WEBPACK FOOTER //
// ./src/Components/Column.tsx